import type { Site, SiteKey } from '@/api/accommodation/types'

export type AggregatedRating = {
  value: number
  count: number
}

const STAR_SCALE = 5

// Each source keeps its native scale (Booking/CS-CHALUPY out of 10, the rest out of 5).
// Normalise to a common 5-star scale before aggregating; a missing scale falls back to 5.
function normaliseToStars(value: number, scale: number | null | undefined): number {
  const effectiveScale = scale && scale > 0 ? scale : STAR_SCALE
  return (value / effectiveScale) * STAR_SCALE
}

export function hasValidRatings(sites: Site[] | undefined): boolean {
  return !!sites?.some((s) => s.ratingValue !== null && s.ratingCount !== null && s.ratingCount > 0)
}

export function aggregateRating(sites: Site[] | undefined): AggregatedRating | undefined {
  if (!sites || sites.length === 0) return undefined

  const valid = sites.filter(
    (s): s is Site & { ratingValue: number; ratingCount: number } =>
      s.ratingValue !== null && s.ratingCount !== null && s.ratingCount > 0,
  )
  if (valid.length === 0) return undefined

  const count = valid.reduce((sum, s) => sum + s.ratingCount, 0)
  const weighted = valid.reduce((sum, s) => sum + normaliseToStars(s.ratingValue, s.ratingScale) * s.ratingCount, 0)

  return { value: weighted / count, count }
}

const ratingFormatters = new Map<number, Intl.NumberFormat>()

function getRatingFormatter(): Intl.NumberFormat {
  let formatter = ratingFormatters.get(2)
  if (!formatter) {
    formatter = new Intl.NumberFormat('cs-CZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    ratingFormatters.set(2, formatter)
  }
  return formatter
}

export function formatRatingValue(value: number): string {
  return getRatingFormatter().format(value)
}

export const SITE_LABELS: Record<SiteKey, string> = {
  airbnb: 'Airbnb',
  amazingplaces: 'Amazing Places',
  booking: 'Booking.com',
  cschalupy: 'CS-CHALUPY',
  echalupy: 'e-chalupy',
  google: 'Google',
  kouzelnechaloupky: 'Kouzelné chaloupky',
}

export const SITE_LOGOS: Record<SiteKey, string> = {
  airbnb: '/logos/airbnb.svg',
  amazingplaces: '/logos/amazingplaces.svg',
  booking: '/logos/booking.svg',
  cschalupy: '/logos/cschalupy.svg',
  echalupy: '/logos/echalupy.svg',
  google: '/logos/google.svg',
  kouzelnechaloupky: '/logos/kouzelnechaloupky.svg',
}
